import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Mail } from "lucide-react";
import Logo from "../components/app/Logo";

/* ------------ Section primitives ------------- */

const Lead = ({ body }) => (
  <p
    className="text-text-secondary mx-auto"
    style={{ fontSize: 17, lineHeight: 1.7, maxWidth: 680 }}
  >
    {body}
  </p>
);

const SectionHeading = ({ kicker, title }) => (
  <div className="mb-8 text-center">
    {kicker && (
      <span
        className="font-mono inline-block mb-3"
        style={{ fontSize: 11, letterSpacing: "0.18em", color: "var(--color-accent)" }}
      >
        {kicker}
      </span>
    )}
    <h2
      className="text-text-primary mx-auto"
      style={{
        fontSize: "clamp(26px, 3.4vw, 36px)",
        fontWeight: 700,
        letterSpacing: "-0.025em",
        lineHeight: 1.15,
        maxWidth: 720,
      }}
    >
      {title}
    </h2>
  </div>
);

const Card = ({ children, accent }) => (
  <div
    className="rounded-[12px] border p-6"
    style={{
      background: "var(--color-background-secondary)",
      borderColor: accent ? "rgba(45,212,191,0.4)" : "var(--color-border-primary)",
    }}
  >
    {children}
  </div>
);

const Features = ({ kicker, title, items, columns = 3 }) => (
  <section className="py-16">
    <SectionHeading kicker={kicker} title={title} />
    <div
      className={`grid gap-5 mt-10 ${
        columns === 2 ? "md:grid-cols-2" : columns === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"
      }`}
    >
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <Card key={i}>
            {Icon && (
              <span
                className="grid place-items-center rounded-[8px] mb-4"
                style={{
                  width: 36,
                  height: 36,
                  background: "var(--color-accent-subtle)",
                  border: "1px solid rgba(45,212,191,0.35)",
                }}
              >
                <Icon size={16} color="var(--color-accent)" />
              </span>
            )}
            <p
              className="text-text-primary mb-2"
              style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              {it.title}
            </p>
            <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.65 }}>
              {it.body}
            </p>
            {it.tag && (
              <span
                className="font-mono rounded-md inline-block mt-3"
                style={{
                  padding: "2px 8px",
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-tertiary)",
                }}
              >
                {it.tag}
              </span>
            )}
          </Card>
        );
      })}
    </div>
  </section>
);

const Prose = ({ kicker, title, body }) => (
  <section className="py-12">
    <SectionHeading kicker={kicker} title={title} />
    <div className="max-w-[720px] mx-auto text-text-secondary space-y-4" style={{ fontSize: 15, lineHeight: 1.8 }}>
      {(Array.isArray(body) ? body : [body]).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  </section>
);

const Checklist = ({ kicker, title, items }) => (
  <section className="py-12">
    <SectionHeading kicker={kicker} title={title} />
    <ul className="max-w-[640px] mx-auto space-y-3">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-text-secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>
          <Check size={16} color="var(--color-accent)" className="shrink-0 mt-0.5" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </section>
);

const Code = ({ kicker, title, language = "hcl", body }) => (
  <section className="py-12">
    <SectionHeading kicker={kicker} title={title} />
    <div className="max-w-[760px] mx-auto">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-text-tertiary" style={{ fontSize: 10, letterSpacing: "0.14em" }}>
            {language.toUpperCase()}
          </span>
        </div>
        <pre
          className="font-mono overflow-auto"
          style={{
            fontSize: 12,
            lineHeight: 1.7,
            color: "var(--color-text-primary)",
            background: "var(--color-background-tertiary)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 8,
            padding: 14,
            whiteSpace: "pre",
          }}
        >
          {body}
        </pre>
      </Card>
    </div>
  </section>
);

const ChangelogList = ({ releases }) => (
  <section className="py-12">
    <div className="max-w-[760px] mx-auto space-y-12">
      {releases.map((r, i) => (
        <div key={i} data-testid={`release-${r.version}`}>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--color-accent)" }}>
              {r.version}
            </span>
            <span style={{ width: 1, height: 14, background: "var(--color-border-primary)" }} />
            <span className="font-mono text-text-tertiary" style={{ fontSize: 11, letterSpacing: "0.06em" }}>
              {r.date}
            </span>
          </div>
          <h3 className="text-text-primary mb-4" style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
            {r.title}
          </h3>
          <ul className="space-y-2.5">
            {r.items.map((it, j) => (
              <li key={j} className="flex gap-3 text-text-secondary" style={{ fontSize: 14, lineHeight: 1.65 }}>
                <span style={{ color: "var(--color-accent)" }}>▸</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

const Roles = ({ positions }) => (
  <section className="py-12">
    <div className="max-w-[760px] mx-auto space-y-3">
      {positions.length === 0 ? (
        <Card>
          <p className="text-text-secondary text-center" style={{ fontSize: 14, lineHeight: 1.7 }}>
            We&apos;re not hiring publicly right now — but if you&apos;re an exceptional engineer or designer, send us a line:{" "}
            <a
              href="mailto:careers@guidedsentry.com"
              style={{ color: "var(--color-accent)", textDecoration: "none" }}
            >
              careers@guidedsentry.com
            </a>
          </p>
        </Card>
      ) : (
        positions.map((p, i) => (
          <Card key={i}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p
                  className="text-text-primary"
                  style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}
                >
                  {p.title}
                </p>
                <p className="font-mono text-text-tertiary mt-1" style={{ fontSize: 11, letterSpacing: "0.06em" }}>
                  {p.team} · {p.type} · {p.location}
                </p>
              </div>
              <a
                href="mailto:careers@guidedsentry.com"
                className="motion-micro inline-flex items-center gap-1.5"
                style={{
                  fontSize: 13,
                  color: "var(--color-accent)",
                  textDecoration: "none",
                  border: "1px solid rgba(45,212,191,0.4)",
                  borderRadius: 8,
                  padding: "6px 12px",
                }}
              >
                Apply <ArrowRight size={13} />
              </a>
            </div>
          </Card>
        ))
      )}
    </div>
  </section>
);

const FAQ = ({ items }) => (
  <section className="py-12">
    <SectionHeading kicker="QUESTIONS" title="Frequently asked" />
    <div className="max-w-[760px] mx-auto space-y-3">
      {items.map((it, i) => (
        <details
          key={i}
          className="rounded-[10px] border group"
          style={{ background: "var(--color-background-secondary)", borderColor: "var(--color-border-primary)" }}
        >
          <summary
            className="cursor-pointer px-5 py-4 flex items-center justify-between text-text-primary"
            style={{ fontSize: 14, fontWeight: 500, listStyle: "none" }}
          >
            {it.q}
            <span
              className="font-mono group-open:rotate-45 motion-micro"
              style={{ color: "var(--color-accent)", display: "inline-block", transition: "transform 200ms" }}
            >
              +
            </span>
          </summary>
          <div className="px-5 pb-5 text-text-secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>
            {it.a}
          </div>
        </details>
      ))}
    </div>
  </section>
);

const ContactCard = () => (
  <section className="py-12">
    <div className="max-w-[640px] mx-auto grid md:grid-cols-2 gap-4">
      {[
        { label: "General", email: "hello@guidedsentry.com" },
        { label: "Sales", email: "sales@guidedsentry.com" },
        { label: "Support", email: "support@guidedsentry.com" },
        { label: "Security", email: "security@guidedsentry.com" },
      ].map((c) => (
        <Card key={c.email}>
          <p className="font-mono text-text-tertiary mb-2" style={{ fontSize: 10, letterSpacing: "0.14em" }}>
            {c.label.toUpperCase()}
          </p>
          <a
            href={`mailto:${c.email}`}
            className="font-mono motion-micro inline-flex items-center gap-2"
            style={{ fontSize: 13, color: "var(--color-accent)", textDecoration: "none" }}
          >
            <Mail size={13} /> {c.email}
          </a>
        </Card>
      ))}
    </div>
  </section>
);

/* ------------- Page shell ------------- */

const renderSection = (s, i) => {
  switch (s.type) {
    case "features":
      return <Features key={i} {...s} />;
    case "prose":
      return <Prose key={i} {...s} />;
    case "checklist":
      return <Checklist key={i} {...s} />;
    case "code":
      return <Code key={i} {...s} />;
    case "changelog":
      return <ChangelogList key={i} releases={s.releases} />;
    case "roles":
      return <Roles key={i} positions={s.positions} />;
    case "faq":
      return <FAQ key={i} items={s.items} />;
    case "contact":
      return <ContactCard key={i} />;
    default:
      return null;
  }
};

export default function RichPage({ section, title, lead, sections = [], pill, cta }) {
  return (
    <div className="min-h-screen bg-bg-primary text-text-primary" data-testid={`rich-page-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <nav
        className="w-full border-b border-border-secondary sticky top-0 z-40"
        style={{ background: "rgba(10,10,10,0.85)", backdropFilter: "blur(12px)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 h-[64px] flex items-center justify-between">
          <Link to="/" style={{ textDecoration: "none" }}>
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="rich-back-home"
            className="motion-micro inline-flex items-center gap-1.5"
            style={{
              fontSize: 13,
              color: "var(--color-text-secondary)",
              height: 36,
              padding: "0 14px",
              borderRadius: 8,
              border: "1px solid var(--color-border-primary)",
              textDecoration: "none",
            }}
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <section className="relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-6 pt-20 pb-12 text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-6"
            style={{
              background: "var(--color-accent-subtle)",
              border: "1px solid rgba(45,212,191,0.4)",
            }}
          >
            <span
              className="font-mono"
              style={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--color-accent)" }}
            >
              {pill || section.toUpperCase()}
            </span>
          </span>
          <h1
            className="text-text-primary mb-5"
            style={{
              fontSize: "clamp(36px, 5vw, 54px)",
              fontWeight: 700,
              letterSpacing: "-0.035em",
              lineHeight: 1.05,
              maxWidth: 880,
              margin: "0 auto",
            }}
          >
            {title}
          </h1>
          {lead && <Lead body={lead} />}
          {cta && (
            <div className="mt-9">
              <Link
                to={cta.to || "/login"}
                className="motion-micro inline-flex items-center gap-2 rounded-[8px]"
                style={{
                  background: "var(--color-accent)",
                  color: "#0a0a0a",
                  height: 44,
                  padding: "0 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: "none",
                  boxShadow: "0 14px 36px -14px rgba(45,212,191,0.55)",
                }}
              >
                {cta.label}
              </Link>
            </div>
          )}
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-6 pb-24">
        {sections.map((s, i) => renderSection(s, i))}
      </main>
      <footer className="border-t border-border-secondary px-6 py-8">
        <nav aria-label="Legal and security" className="max-w-[1100px] mx-auto flex flex-wrap gap-6 text-sm text-text-secondary">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/company/security">Security</Link>
        </nav>
      </footer>
    </div>
  );
}
